import {useMemo} from 'react';
import {kr, pct, tickLabels} from '../../lib/format.js';
import {DASHES, niceScale, Grid, useHover, HitBands, Tooltip, Legend} from './primitives.jsx';

const LINES = [
	{key: 'gross_profit', label: 'Bruttovinst', dash: null},
	{key: 'ebitda', label: 'EBITDA', dash: '6 4'},
];
const MARGINS = [
	{key: 'gross_margin', label: 'Bruttomarginal', dash: null},
	{key: 'ebitda_margin', label: 'EBITDA-marginal', dash: '6 4'},
];
const W = 560, H = 250, SH = 78, L = 52, R = 10, T = 10, B = 24;

/**
 * Kronor above, percent below. They are NOT put on one plot with two axes — that is
 * the dual-axis mistake, and with a margin that runs to -250% it would also flatten
 * the kronor lines into a straight line.
 */
export default function Profitability({months, per = 'månad'}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(months), [months]);
	const g = useMemo(() => {
		const vals = months.flatMap(m => LINES.map(l => m[l.key] ?? 0));
		const sc = niceScale(Math.min(0, ...vals), Math.max(0, ...vals));
		const iw = W - L - R, ih = H - T - B;
		const y = v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih;
		const x = i => L + (iw * (i + 0.5)) / months.length;
		// A margin is a ratio, so a month with almost no revenue produces an absurd one
		// — 2021 has months where a few hundred kronor of revenue gives five-digit
		// percentages, which flattens every real margin into the zero line. The SCALE is
		// therefore built only from months with meaningful revenue; outlier months are
		// still drawn, clipped to the edge, rather than silently dropped.
		// A margin is a ratio, so a month with almost no revenue produces an absurd one:
		// early 2021 months give five-digit percentages that flatten every real margin
		// onto the zero line. The scale is therefore built from the 5th-95th PERCENTILE
		// rather than min/max — outliers are still drawn, clipped to the edge, never
		// silently dropped, and never allowed to set the axis.
		const mvals = months.flatMap(l => MARGINS.map(s => l[s.key]))
			.filter(v => Number.isFinite(v)).sort((a, b) => a - b);
		const at = f => mvals[Math.min(mvals.length - 1, Math.max(0, Math.round(f * (mvals.length - 1))))] ?? 0;
		// Bounded as well as percentile-based. Pifa AB's own p5 is -469%, and rounding
		// that out to a "nice" step gives a +/-1000% axis on which every real margin is
		// a flat line. Beyond -200%/+150% the exact number stops carrying information —
		// the reader needs "deeply negative", not the digit — so the axis stops there and
		// the outliers clip.
		const lo = Math.max(Math.min(0, at(0.05)), -2);
		const hi = Math.min(Math.max(0, at(0.95)), 1.5);
		const ms = mvals.length ? niceScale(lo, hi, 3) : {lo: 0, hi: 1, step: 1};
		const my = v => {
			const c = Math.min(Math.max(v, ms.lo), ms.hi);
			return 10 + (SH - 30) - ((c - ms.lo) / (ms.hi - ms.lo)) * (SH - 30);
		};
		return {y, x, sc, ms, my};
	}, [months]);

	if (!months.length) return null;

	const path = (key, y) => months
		.map((m, i) => (m[key] == null ? null : `${g.x(i)},${y(m[key])}`))
		.filter(Boolean).join(' ');

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">Lönsamhet per {per}</span>
				<span className="chart__hint">bruttovinst och EBITDA. Marginalerna visas separat — de är procent, inte kronor.</span>
			</div>
			<div className="chart__canvas chart__canvas--tall">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label={`Bruttovinst och EBITDA per ${per}`}>
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{LINES.map(l => (
						<polyline key={l.key} points={path(l.key, g.y)} fill="none" stroke="#000"
							strokeWidth={2} strokeDasharray={l.dash ?? undefined} strokeLinejoin="round" />
					))}
					{months.length <= 24 && LINES.map(l => months.map((m, i) => (m[l.key] == null ? null : (
						<circle key={`${l.key}-${m.month}`} cx={g.x(i)} cy={g.y(m[l.key])} r={2.5}
							fill={l.dash ? '#fff' : '#000'} stroke="#000" strokeWidth={1.2} />
					))))}
					{ticks.map((t, i) => (t == null ? null : (
						<text key={months[i].month} x={g.x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={months} x={g.x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={months} at={at} x={g.x} W={W} title={m => m.label ?? m.month} rows={m => [
					['Bruttovinst', `${kr(m.gross_profit)} kr`], ['Bruttomarginal', pct(m.gross_margin)],
					['EBITDA', `${kr(m.ebitda)} kr`], ['EBITDA-marginal', pct(m.ebitda_margin)],
					['Omsättning', kr(m.revenue)],
				]} />
			</div>
			<div className="chart__striplabel">Marginal</div>
			<div className="chart__strip">
				<svg viewBox={`0 0 ${W} ${SH}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
					<line x1={L} x2={W - R} y1={g.my(0)} y2={g.my(0)} className="zl" />
					<text x={L - 6} y={g.my(g.ms.hi) + 3.5} className="ax" textAnchor="end">
						{Math.round(g.ms.hi * 100)}%</text>
					<text x={L - 6} y={g.my(g.ms.lo) + 3.5} className="ax" textAnchor="end">
						{Math.round(g.ms.lo * 100)}%</text>
					{MARGINS.map(l => (
						<polyline key={l.key} points={path(l.key, g.my)} fill="none" stroke="#000"
							strokeWidth={1.5} strokeDasharray={l.dash ?? undefined} />
					))}
				</svg>
			</div>
			<Legend items={[...LINES, ...MARGINS].map(l => ({label: l.label, dash: l.dash}))} />
		</div>
	);
}
